// AriaCues.jsx — HER ASSIST CUES, IN THE WEB APP (Braeden 8/16)
//
//   "when we do the onbaording ... she needs to use assist mode features to
//    show me what she does"
//
// The real cue overlay lives in Electron (attention-overlay.html) and paints on
// the whole desktop. During onboarding she is in a browser tab, so none of it
// is available — she could only ever TALK about what she does.
//
// This is the same visual language, scoped to the page: the dot, a region box,
// a spotlight, an arrow, step badges, a popup and a status line. Same colours,
// same meanings, same rules as services/ariaAssistCues.js — so what she does
// here teaches the same vocabulary she will use on the desktop, and a person
// who has seen her point in the browser recognises it when she takes the screen.
//
// TARGETS ARE CSS SELECTORS, NOT COORDINATES. She is pointing at HER OWN app,
// and its layout moves with the viewport — a hardcoded x/y would be wrong on
// every screen but the one it was written on. The element is measured at draw
// time and re-measured on resize.
import React from 'react';

const BLUE = '#0a84ff';

// 'text:Download' targets — resolve by what the control SAYS, not a CSS
// selector (8/20: "she should be able to read the screen, point to this").
// This is what lets her point at anything she can read: the same labels her
// screen snapshot ships to the backend can be pointed at by name.
function findByText(term) {
  const t = String(term).toLowerCase();
  for (const el of document.querySelectorAll('button,[role="button"],a[href],input,textarea')) {
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height || r.bottom < 0 || r.top > window.innerHeight) continue;
    const label = ((el.getAttribute('aria-label') || '') + ' ' + (el.textContent || '') + ' ' + (el.placeholder || '')).toLowerCase();
    if (label.includes(t)) return el;
  }
  return null;
}

function useBox(selector, deps) {
  const [box, setBox] = React.useState(null);
  React.useEffect(() => {
    if (!selector) { setBox(null); return undefined; }
    const measure = () => {
      try {
        const el = selector.startsWith('text:')
          ? findByText(selector.slice(5))
          : document.querySelector(selector);
        if (!el) { setBox(null); return; }
        const r = el.getBoundingClientRect();
        if (!r.width || !r.height) { setBox(null); return; }
        setBox({ x: r.left, y: r.top, w: r.width, h: r.height });
      } catch (_) { setBox(null); }
    };
    measure();
    // the thing she is pointing at can arrive late (a panel that just opened)
    const t = setInterval(measure, 400);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearInterval(t);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [selector, ...(deps || [])]);
  return box;
}

/**
 * @param {object} cue  one of:
 *   { kind:'dot',      at }                      about to click
 *   { kind:'region',   at, label }               talking about an area
 *   { kind:'spotlight',at, label }               they are lost
 *   { kind:'arrow',    from, to, label }         this came from that
 *   { kind:'steps',    items:[{at,label}] }      several, in order
 *   { kind:'popup',    at, title, body }         something to read
 *   { kind:'status',   text }                    long work in progress
 * `at`/`from`/`to` are CSS selectors.
 */
export default function AriaCues({ cue }) {
  const at = cue && (cue.at || null);
  const box = useBox(at, [cue && cue.kind]);
  const fromBox = useBox(cue && cue.from, [cue && cue.kind]);
  const toBox = useBox(cue && cue.to, [cue && cue.kind]);
  // steps measure their own targets, so the hook count stays stable
  const s0 = useBox(cue && cue.kind === 'steps' && cue.items[0] ? cue.items[0].at : null, [cue]);
  const s1 = useBox(cue && cue.kind === 'steps' && cue.items[1] ? cue.items[1].at : null, [cue]);
  const s2 = useBox(cue && cue.kind === 'steps' && cue.items[2] ? cue.items[2].at : null, [cue]);

  if (!cue) return null;
  const K = cue.kind;

  // everything sits in one fixed, click-through layer above the app
  const layer = {
    position: 'fixed', inset: 0, zIndex: 9998, pointerEvents: 'none',
  };

  if (K === 'status') {
    return (
      <div style={layer}>
        <div style={{
          position: 'absolute', left: '50%', bottom: 96, transform: 'translateX(-50%)',
          background: 'rgba(10,20,36,.94)', border: `1px solid ${BLUE}`, borderRadius: 99,
          padding: '7px 16px', fontSize: 12.5, fontWeight: 700, color: '#dceaff',
          boxShadow: `0 0 22px rgba(10,132,255,.35)`,
        }}>{cue.text}</div>
      </div>
    );
  }

  if (K === 'dot' && box) {
    const cx = box.x + box.w / 2, cy = box.y + box.h / 2;
    return (
      <div style={layer}>
        <div style={{
          position: 'absolute', left: cx - 13, top: cy - 13, width: 26, height: 26,
          borderRadius: 99, background: 'rgba(10,132,255,.9)',
          boxShadow: '0 0 0 6px rgba(10,132,255,.22), 0 0 26px rgba(10,132,255,.8)',
          transition: 'left .45s cubic-bezier(.22,.61,.36,1), top .45s cubic-bezier(.22,.61,.36,1)',
        }} />
      </div>
    );
  }

  if ((K === 'region' || K === 'spotlight') && box) {
    const pad = 8;
    return (
      <div style={layer}>
        {K === 'spotlight' && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(4,8,16,.62)',
            // punch a hole over the target so everything else reads as dimmed
            WebkitMaskImage: `radial-gradient(circle at ${box.x + box.w / 2}px ${box.y + box.h / 2}px, transparent ${Math.max(box.w, box.h) * 0.75}px, #000 ${Math.max(box.w, box.h) * 0.78}px)`,
            maskImage: `radial-gradient(circle at ${box.x + box.w / 2}px ${box.y + box.h / 2}px, transparent ${Math.max(box.w, box.h) * 0.75}px, #000 ${Math.max(box.w, box.h) * 0.78}px)`,
          }} />
        )}
        <div style={{
          position: 'absolute', left: box.x - pad, top: box.y - pad,
          width: box.w + pad * 2, height: box.h + pad * 2,
          border: `1.5px solid ${BLUE}`, borderRadius: 12,
          background: 'rgba(10,132,255,.05)',
          boxShadow: '0 0 0 1px rgba(10,132,255,.25), 0 0 26px rgba(10,132,255,.18)',
          animation: 'ariaCuePulse 2.4s ease-in-out infinite',
        }}>
          {cue.label && (
            <span style={{
              position: 'absolute', top: -11, left: 14, background: BLUE, color: '#fff',
              fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase',
              padding: '2px 9px', borderRadius: 5, whiteSpace: 'nowrap',
            }}>{cue.label}</span>
          )}
        </div>
        <style>{`@keyframes ariaCuePulse{0%,100%{box-shadow:0 0 0 1px rgba(10,132,255,.25),0 0 22px rgba(10,132,255,.14)}50%{box-shadow:0 0 0 2px rgba(10,132,255,.45),0 0 34px rgba(10,132,255,.30)}}`}</style>
      </div>
    );
  }

  // ── AN ARROW THAT POINTS AT ONE THING (Braeden 8/16) ────────────────────
  //   "make an arrow in assist mode that points, so she can say look at the
  //    arrow on the boom of the screen, type in your website here"
  //
  // The existing arrow needs a FROM and a TO — it expresses a relationship
  // between two things. This one has a single target: it comes in from above,
  // lands on the thing, and carries the instruction. That is what "type it in
  // here" needs, and there was no cue for it.
  if (K === 'point' && box) {
    const cx = box.x + box.w / 2;
    const tipY = box.y - 10;          // just above the target
    const tailY = Math.max(12, tipY - 84);
    return (
      <div style={layer}>
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
          <defs>
            <marker id="ariaPointHead" markerWidth="11" markerHeight="11" refX="8" refY="5.5" orient="auto">
              <path d="M0,0 L11,5.5 L0,11 Z" fill={BLUE} />
            </marker>
          </defs>
          <line x1={cx} y1={tailY} x2={cx} y2={tipY} stroke={BLUE} strokeWidth="3"
                strokeLinecap="round" markerEnd="url(#ariaPointHead)">
            {/* a still arrow reads as decoration; a moving one reads as "here" */}
            <animate attributeName="y2" values={`${tipY - 7};${tipY};${tipY - 7}`}
                     dur="1.1s" repeatCount="indefinite" />
          </line>
        </svg>
        {cue.label && (
          <div style={{
            position: 'absolute', left: cx, top: tailY - 26, transform: 'translateX(-50%)',
            background: BLUE, color: '#fff', fontSize: 11, fontWeight: 800,
            letterSpacing: '.1em', textTransform: 'uppercase', padding: '4px 11px',
            borderRadius: 6, whiteSpace: 'nowrap', boxShadow: '0 0 16px rgba(10,132,255,.5)',
          }}>{cue.label}</div>
        )}
      </div>
    );
  }

  if (K === 'arrow' && fromBox && toBox) {
    const x1 = fromBox.x + fromBox.w / 2, y1 = fromBox.y + fromBox.h / 2;
    const x2 = toBox.x + toBox.w / 2, y2 = toBox.y + toBox.h / 2;
    return (
      <div style={layer}>
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
          <defs>
            <marker id="ariaArrowHead" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto">
              <path d="M0,0 L9,4.5 L0,9 Z" fill={BLUE} />
            </marker>
          </defs>
          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={BLUE} strokeWidth="2.5"
                strokeLinecap="round" markerEnd="url(#ariaArrowHead)" opacity="0.95" />
        </svg>
      </div>
    );
  }

  if (K === 'steps') {
    const boxes = [s0, s1, s2];
    return (
      <div style={layer}>
        {(cue.items || []).slice(0, 3).map((it, i) => {
          const b = boxes[i];
          if (!b) return null;
          return (
            <div key={i} style={{
              position: 'absolute', left: b.x + b.w / 2 - 15, top: b.y + b.h / 2 - 15,
              width: 30, height: 30, borderRadius: 99, background: BLUE, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 800,
              boxShadow: '0 0 20px rgba(10,132,255,.7)',
              animation: `ariaStepIn .4s ease ${i * 200}ms both`,
            }}>{i + 1}</div>
          );
        })}
        <style>{`@keyframes ariaStepIn{from{opacity:0;transform:scale(.5)}to{opacity:1;transform:scale(1)}}`}</style>
      </div>
    );
  }

  if (K === 'popup' && box) {
    return (
      <div style={layer}>
        <div style={{
          position: 'absolute', left: Math.min(box.x, window.innerWidth - 320),
          top: box.y + box.h + 12, width: 300,
          background: 'rgba(10,20,36,.97)', border: `1px solid ${BLUE}`, borderRadius: 11,
          padding: '11px 13px', boxShadow: '0 14px 40px rgba(0,0,0,.55)',
        }}>
          {cue.title && <div style={{ fontSize: 12.5, fontWeight: 800, marginBottom: 3 }}>{cue.title}</div>}
          <div style={{ fontSize: 12, color: '#b9cbe6', lineHeight: 1.5 }}>{cue.body}</div>
        </div>
      </div>
    );
  }

  return null;
}
